import React, { useRef } from 'react';
import { JarallaxOptions } from 'jarallax';
import classnames from 'classnames';

import { useBreakpointMatch } from 'src/hooks/useBreakpoint';

import { useJarallax } from '../../hooks/';
import type { RowType } from '../rowConfigAggregator';
import type { CustomCSSProperties } from '../../../types/pbTypes';
import { PageBuilderElementWrapper } from '../../PageBuilderElementWrapper';

import classes from './Row.module.scss';

type RowProps = RowType & {
    styles: CustomCSSProperties;
};

/**
 * Return the PB Row Block
 * @param props
 * @constructor
 */
export function Row(props: RowProps) {
    const { isDesk } = useBreakpointMatch();

    const backgroundElement = useRef<HTMLDivElement | null>(null);
    const backgroundImage = isDesk ? props.desktopImage : props.mobileImage;
    const hasBackground = props.desktopImage || props.backgroundColor || props.videoSrc;

    const hasVideoOverlay = props.videoOverlayColor !== undefined;

    const videoOverlayStyles = {
        backgroundColor: props.videoOverlayColor,
    };

    /**
     * If the background type is video or parallax is enabled, we need to enable the parallax effect
     */
    const isParallaxEnabled = Boolean(props.backgroundType === 'video' || props.enableParallax);
    const parallaxSpeed = !props.enableParallax ? 1 : props.parallaxSpeed;

    const config: JarallaxOptions = {
        imgPosition: props.backgroundPosition,
        imgRepeat: props.backgroundRepeat === 'no-repeat' ? 'no-repeat' : 'repeat',
        imgSize: props.backgroundSize,
        imgSrc: backgroundImage,
        speed: parallaxSpeed,
        videoLazyLoading: props.videoLazyLoading,
        videoLoop: props.videoLoop,
        videoPlayOnlyVisible: props.videoPlayOnlyVisible,
        videoSrc: props.videoSrc,
    };

    useJarallax({
        isEnabled: isParallaxEnabled,
        backgroundElement,
        config,
        backgroundType: props.backgroundType,
    });

    const rowClassNames: string[] = [classes[`root-${props.appearance}`], 'pageBuilderRowGlobal'];
    const rowContainedClassNames: string[] = [classes['rootContentContained'], 'pageBuilderRowContainedGlobal'];
    const rowContainedContentClassNames: string[] = [classes['rootContent'], 'pageBuilderRowRootContentGlobal'];

    /**
     * If the row has a background color/image/video and the appearance is contained,
     * we need to add a specific class to the row to allow us to add a padding to the inrternal content.
     * This is so the content is not touching the edges of the row.
     */
    if (hasBackground && props.appearance === 'contained') {
        rowClassNames.push(classes['rootContentContainedHasBackground']);
    }

    if (props.appearance !== 'contained' && props.cssClasses && props.cssClasses?.length > 0) {
        rowClassNames.push(...props.cssClasses);
    }

    if (props.appearance === 'contained' && props.cssClasses && props.cssClasses?.length > 0) {
        rowContainedClassNames.push(...props.cssClasses);
    }

    /**
     * If the appearance is not contained, we need to render an edge-to-edge wrapper
     * if the appearance is fullwidth, it should be contained but edge-to-edge
     * if the appearance is full-bleed, it should be edge-to-edge
     */
    if (props.appearance !== 'contained') {
        return (
            <PageBuilderElementWrapper
                contentType={props.contentType}
                uniqueID={props.uniqueID}
                styles={props.styles}
                classNames={rowClassNames}
                ref={backgroundElement}
            >
                {hasVideoOverlay && <div className={classes['videoOverlay']} style={videoOverlayStyles} />}

                <div
                    className={classnames({
                        [classes['rootContentContained']]: props.appearance === 'full-width',
                        [classes['rootContentContainedFull']]: props.appearance === 'full-bleed',
                    })}
                >
                    <div className={classes['rootContent']}>{props.children}</div>
                </div>
            </PageBuilderElementWrapper>
        );
    }

    /**
     * If the appearance is contained, we need to render a contained wrapper
     * with the content inside of it and the appropriate styles
     */
    return (
        <PageBuilderElementWrapper
            contentType={props.contentType}
            uniqueID={props.uniqueID}
            classNames={rowContainedClassNames}
        >
            <div
                ref={backgroundElement}
                id={`container-${props.uniqueID}`}
                style={props.styles}
                className={rowClassNames.join(' ')}
            >
                {hasVideoOverlay && <div className={classes['videoOverlay']} style={videoOverlayStyles} />}

                <div className={rowContainedContentClassNames.join(' ')}>{props.children}</div>
            </div>
        </PageBuilderElementWrapper>
    );
}
